// apps/web/src/pages/cadastros/novo-cliente-page.tsx — Fase 09, Plano 05
// (CAD-01, OMIE-06, OMIE-09, D-02, D-05, D-09, D-10, D-11, NFR-08).
//
// Página dedicada e contínua (D-02) do cadastro unificado de Cliente: identificação,
// contato, endereço com CEP resiliente (D-09/D-10/D-11), classificação fiscal,
// observações e a escolha (default-on, D-05) de sincronizar com a Omie. Salva via
// POST /api/v1/cadastros/clientes com sessão/CSRF (apiMutate) e nunca chama
// ViaCEP/BrasilAPI diretamente — a consulta de CEP sempre passa pela API própria
// (GET /api/v1/cadastros/cep/:cep, apiFetch). Nenhum dado é persistido em
// storage/console: o estado só existe em memória até o salvamento (T-09-05-03).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { FormDialog, FormDialogContent, FormDialogHeader, FormDialogTitle, FormDialogDescription, FormDialogBody, FormDialogFooter } from '../../components/ui/form-dialog';
import type { ClienteEntity, ConsultaCepResultado } from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';
import {
  aplicarResultadoCep,
  estadoInicialNovoCliente,
  iniciarConsultaCep,
  montarPayloadCriarCliente,
  normalizarCep,
  podeConsultarCep,
  type ContatoFiscalFormState,
  type DadosFiscaisComplementaresFormState,
  type EnderecoFiscalFormState,
  type NovoClienteFormState,
} from '../../lib/novo-cliente-form';
import { CamposContatoCliente } from './cliente-form';
import { CampoSelect } from '../../components/ui/campo-select';

// D-11: mensagem contextual não bloqueante para cada status de consulta de CEP —
// sempre combinada com um ícone (nunca só cor) para permanecer legível em tablet.
const CEP_STATUS_MENSAGEM: Record<string, { texto: string; tom: 'info' | 'sucesso' | 'aviso' }> = {
  loading: { texto: 'Consultando CEP…', tom: 'info' },
  success: { texto: 'Endereço preenchido a partir do CEP. Revise e edite se necessário.', tom: 'sucesso' },
  'not-found': { texto: 'CEP não encontrado nos provedores. Preencha o endereço manualmente.', tom: 'aviso' },
  unavailable: { texto: 'Consulta de CEP indisponível no momento. Preencha o endereço manualmente.', tom: 'aviso' },
};

// O router (apps/web/src/router.tsx) já escuta `popstate` para trocar de tela sem
// reload; despachar o evento aqui é a forma de navegar programaticamente sem expor
// um `navigate()` novo no router (evita mudança arquitetural fora de escopo).
function navegarParaListaComNovoCliente(clienteId: string): void {
  const destino = `/cadastros?novoClienteId=${encodeURIComponent(clienteId)}`;
  window.history.pushState(null, '', destino);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// NFR-08: min-h-[44px] garante touch target adequado em tablet sem hover.
const inputBaseClass =
  'w-full min-h-[44px] px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 bg-white';
const labelBaseClass = 'block text-xs font-semibold text-slate-700 mb-1';

export const NovoClientePage: React.FC = () => {
  const [formState, setFormState] = useState<NovoClienteFormState>(estadoInicialNovoCliente);
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Fonte única da sequência de requisição de CEP: incrementada de forma síncrona
  // antes de qualquer chamada assíncrona, para que aplicarResultadoCep sempre
  // descarte respostas de requisições supersedidas (D-09).
  const requestSequenceRef = useRef(0);

  const executarConsultaCep = useCallback((cepNormalizado: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    requestSequenceRef.current += 1;
    const sequenciaDaRequisicao = requestSequenceRef.current;

    setFormState((prev) => iniciarConsultaCep(prev, cepNormalizado));

    apiFetch<ConsultaCepResultado>(`/api/v1/cadastros/cep/${cepNormalizado}`, {
      signal: controller.signal,
    })
      .then((resultado) => {
        setFormState((prev) => aplicarResultadoCep(prev, resultado, sequenciaDaRequisicao));
      })
      .catch(() => {
        // Requisição abortada por uma consulta mais recente: nada a fazer, a mais
        // recente já assumiu a sequência atual. Qualquer outro erro (rede/5xx) vira
        // aviso não bloqueante — nunca desabilita edição manual ou Salvar (D-11).
        if (controller.signal.aborted) return;
        setFormState((prev) => aplicarResultadoCep(prev, { status: 'INDISPONIVEL' }, sequenciaDaRequisicao));
      });
  }, []);

  // D-09: dispara automaticamente ao alcançar oito dígitos; não repete a mesma
  // consulta enquanto o usuário não editar o campo de novo — "Buscar novamente" é
  // o único caminho para refazer a consulta do mesmo CEP.
  useEffect(() => {
    const normalizado = normalizarCep(formState.enderecoFiscal.cep);
    if (!podeConsultarCep(normalizado)) return;
    if (formState.cepLookup.cep === normalizado) return;
    executarConsultaCep(normalizado);
    // Dependência intencionalmente restrita ao CEP digitado: `executarConsultaCep`
    // é estável (useCallback com deps vazias) e `formState.cepLookup.cep` é lido do
    // fechamento mais recente a cada disparo, não precisa entrar no array.
  }, [formState.enderecoFiscal.cep]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const atualizarCampo = useCallback(
    <K extends keyof NovoClienteFormState>(campo: K, valor: NovoClienteFormState[K]) => {
      setFormState((prev) => ({ ...prev, [campo]: valor }));
    },
    [],
  );

  const atualizarContato = useCallback(
    <K extends keyof ContatoFiscalFormState>(campo: K, valor: ContatoFiscalFormState[K]) => {
      setFormState((prev) => ({ ...prev, contatoFiscal: { ...prev.contatoFiscal, [campo]: valor } }));
    },
    [],
  );

  const atualizarEndereco = useCallback(
    <K extends keyof EnderecoFiscalFormState>(campo: K, valor: EnderecoFiscalFormState[K]) => {
      setFormState((prev) => ({ ...prev, enderecoFiscal: { ...prev.enderecoFiscal, [campo]: valor } }));
    },
    [],
  );

  const atualizarFiscal = useCallback(
    <K extends keyof DadosFiscaisComplementaresFormState>(
      campo: K,
      valor: DadosFiscaisComplementaresFormState[K],
    ) => {
      setFormState((prev) => ({
        ...prev,
        dadosFiscaisComplementares: { ...prev.dadosFiscaisComplementares, [campo]: valor },
      }));
    },
    [],
  );

  const cepNormalizadoAtual = normalizarCep(formState.enderecoFiscal.cep);
  const podeBuscarCepNovamente = podeConsultarCep(cepNormalizadoAtual);
  const cepStatusInfo = CEP_STATUS_MENSAGEM[formState.cepLookup.status];

  const handleSalvar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setErroSalvar(null);
    setSalvando(true);
    try {
      const payload = montarPayloadCriarCliente(formState);
      const response = await apiMutate('/api/v1/cadastros/clientes', { method: 'POST', body: payload });
      if (response.status !== 201) {
        const corpo = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(corpo.message || 'Não foi possível salvar o cliente. Verifique os dados e tente novamente.');
      }
      const cliente = (await response.json()) as Pick<ClienteEntity, 'id'>;
      navegarParaListaComNovoCliente(cliente.id);
    } catch (err) {
      setErroSalvar(err instanceof Error ? err.message : 'Falha ao salvar o cadastro.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <FormDialog open onOpenChange={(open) => {
      if (!open) {
        window.history.pushState(null, '', '/cadastros');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }}>
      <FormDialogContent>
      <form onSubmit={(event) => void handleSalvar(event)}>
        <FormDialogHeader>
          <FormDialogTitle>Novo cliente</FormDialogTitle>
          <FormDialogDescription>Cadastro completo local, com sincronização opcional com a Omie.</FormDialogDescription>
        </FormDialogHeader>
        <FormDialogBody className="space-y-8">
        {erroSalvar && (
          <div
            className="p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-700 flex items-center gap-2"
            role="alert"
          >
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" aria-hidden="true" />
            <span>{erroSalvar}</span>
          </div>
        )}

        {/* D-02: seis seções contínuas, na ordem exigida — Identificação, Contato,
            Endereço, Classificação Fiscal, Observações, Integração Omie. */}
        <section aria-labelledby="secao-identificacao" className="space-y-4">
          <h2 id="secao-identificacao" className="text-base font-semibold text-slate-900 border-b pb-2">
            Identificação
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="campo-codigo" className={labelBaseClass}>
                Código do cliente <span className="text-rose-500">*</span>
              </label>
              <input
                id="campo-codigo"
                required
                className={inputBaseClass}
                placeholder="Ex: CLI-001"
                value={formState.codigo}
                onChange={(e) => atualizarCampo('codigo', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-nome" className={labelBaseClass}>
                Razão social / Nome <span className="text-rose-500">*</span>
              </label>
              <input
                id="campo-nome"
                required
                className={inputBaseClass}
                placeholder="Ex: Metalúrgica Exemplo S.A."
                value={formState.nome}
                onChange={(e) => atualizarCampo('nome', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-nome-fantasia" className={labelBaseClass}>
                Nome fantasia
              </label>
              <input
                id="campo-nome-fantasia"
                className={inputBaseClass}
                placeholder="Ex: Metalúrgica Exemplo"
                value={formState.nomeFantasia}
                onChange={(e) => atualizarCampo('nomeFantasia', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-documento" className={labelBaseClass}>
                CPF ou CNPJ <span className="text-rose-500">*</span>
              </label>
              <input
                id="campo-documento"
                required
                className={inputBaseClass}
                placeholder="Ex: 98.765.432/0001-10"
                value={formState.documentoFiscal}
                onChange={(e) => atualizarCampo('documentoFiscal', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-tipo-pessoa" className={labelBaseClass}>
                Tipo de pessoa
              </label>
              <CampoSelect
                id="campo-tipo-pessoa"
                value={formState.tipoPessoa}
                onValueChange={(valor) => atualizarCampo('tipoPessoa', valor as NovoClienteFormState['tipoPessoa'])}
                options={[
                  { value: '', label: 'Derivar do documento' },
                  { value: 'PF', label: 'Pessoa física' },
                  { value: 'PJ', label: 'Pessoa jurídica' },
                ]}
              />
            </div>
          </div>
        </section>

        <section aria-labelledby="secao-contato" className="space-y-4">
          <h2 id="secao-contato" className="text-base font-semibold text-slate-900 border-b pb-2">
            Contato
          </h2>
          <CamposContatoCliente contato={formState.contatoFiscal} onChange={atualizarContato} />
        </section>

        <section aria-labelledby="secao-endereco" className="space-y-4">
          <h2 id="secao-endereco" className="text-base font-semibold text-slate-900 border-b pb-2">
            Endereço
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="campo-cep" className={labelBaseClass}>
                CEP
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="campo-cep"
                  className={inputBaseClass}
                  placeholder="00000-000"
                  inputMode="numeric"
                  value={formState.enderecoFiscal.cep}
                  onChange={(e) => atualizarEndereco('cep', e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (podeBuscarCepNovamente) executarConsultaCep(cepNormalizadoAtual);
                  }}
                  disabled={!podeBuscarCepNovamente}
                  className="shrink-0 min-h-[44px] px-3 border border-slate-300 rounded-md text-xs font-semibold text-slate-700 bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                >
                  Buscar novamente
                </button>
              </div>
              {cepStatusInfo && (
                <p
                  role="status"
                  aria-live="polite"
                  className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${
                    cepStatusInfo.tom === 'sucesso'
                      ? 'text-emerald-700'
                      : cepStatusInfo.tom === 'aviso'
                        ? 'text-amber-700'
                        : 'text-slate-500'
                  }`}
                >
                  {formState.cepLookup.status === 'loading' && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                  )}
                  {formState.cepLookup.status === 'success' && (
                    <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                  {(formState.cepLookup.status === 'not-found' || formState.cepLookup.status === 'unavailable') && (
                    <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                  )}
                  {cepStatusInfo.texto}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="campo-uf" className={labelBaseClass}>
                UF
              </label>
              <input
                id="campo-uf"
                maxLength={2}
                className={inputBaseClass}
                placeholder="RS"
                value={formState.enderecoFiscal.estado}
                onChange={(e) => atualizarEndereco('estado', e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="campo-logradouro" className={labelBaseClass}>
                Logradouro
              </label>
              <input
                id="campo-logradouro"
                className={inputBaseClass}
                placeholder="Ex: Av Industrial"
                value={formState.enderecoFiscal.endereco}
                onChange={(e) => atualizarEndereco('endereco', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-numero" className={labelBaseClass}>
                Número
              </label>
              <input
                id="campo-numero"
                className={inputBaseClass}
                placeholder="Ex: 500"
                value={formState.enderecoFiscal.enderecoNumero}
                onChange={(e) => atualizarEndereco('enderecoNumero', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-complemento" className={labelBaseClass}>
                Complemento
              </label>
              <input
                id="campo-complemento"
                className={inputBaseClass}
                placeholder="Ex: Galpão 2"
                value={formState.enderecoFiscal.complemento}
                onChange={(e) => atualizarEndereco('complemento', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-bairro" className={labelBaseClass}>
                Bairro
              </label>
              <input
                id="campo-bairro"
                className={inputBaseClass}
                placeholder="Ex: Distrito Industrial"
                value={formState.enderecoFiscal.bairro}
                onChange={(e) => atualizarEndereco('bairro', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-cidade" className={labelBaseClass}>
                Cidade
              </label>
              <input
                id="campo-cidade"
                className={inputBaseClass}
                placeholder="Ex: Caxias do Sul"
                value={formState.enderecoFiscal.cidade}
                onChange={(e) => atualizarEndereco('cidade', e.target.value)}
              />
            </div>
          </div>
        </section>

        <section aria-labelledby="secao-classificacao-fiscal" className="space-y-4">
          <h2 id="secao-classificacao-fiscal" className="text-base font-semibold text-slate-900 border-b pb-2">
            Classificação Fiscal
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="campo-ie" className={labelBaseClass}>
                Inscrição estadual
              </label>
              <input
                id="campo-ie"
                className={inputBaseClass}
                value={formState.dadosFiscaisComplementares.inscricaoEstadual}
                onChange={(e) => atualizarFiscal('inscricaoEstadual', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="campo-im" className={labelBaseClass}>
                Inscrição municipal
              </label>
              <input
                id="campo-im"
                className={inputBaseClass}
                value={formState.dadosFiscaisComplementares.inscricaoMunicipal}
                onChange={(e) => atualizarFiscal('inscricaoMunicipal', e.target.value)}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                id="campo-simples"
                type="checkbox"
                className="w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                checked={formState.dadosFiscaisComplementares.optanteSimplesNacional === true}
                onChange={(e) => atualizarFiscal('optanteSimplesNacional', e.target.checked)}
              />
              <label htmlFor="campo-simples" className="text-sm text-slate-700 select-none">
                Optante pelo Simples Nacional
              </label>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="campo-obs-fiscal" className={labelBaseClass}>
                Observação fiscal
              </label>
              <textarea
                id="campo-obs-fiscal"
                rows={2}
                className={inputBaseClass}
                value={formState.dadosFiscaisComplementares.observacao}
                onChange={(e) => atualizarFiscal('observacao', e.target.value)}
              />
            </div>
          </div>
        </section>

        <section aria-labelledby="secao-observacoes" className="space-y-4">
          <h2 id="secao-observacoes" className="text-base font-semibold text-slate-900 border-b pb-2">
            Observações
          </h2>
          <div>
            <label htmlFor="campo-observacoes" className={labelBaseClass}>
              Observações gerais
            </label>
            <textarea
              id="campo-observacoes"
              rows={3}
              className={inputBaseClass}
              value={formState.observacoes}
              onChange={(e) => atualizarCampo('observacoes', e.target.value)}
            />
          </div>
        </section>

        <section aria-labelledby="secao-omie" className="space-y-3">
          <h2 id="secao-omie" className="text-base font-semibold text-slate-900 border-b pb-2">
            Integração Omie
          </h2>
          <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4">
            <input
              id="campo-sincronizar-omie"
              type="checkbox"
              className="mt-0.5 w-5 h-5 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              checked={formState.sincronizarComOmie}
              onChange={(e) => atualizarCampo('sincronizarComOmie', e.target.checked)}
            />
            <label htmlFor="campo-sincronizar-omie" className="text-sm text-slate-700">
              <span className="font-semibold text-slate-900 block">Sincronizar com a Omie</span>
              O cliente é sempre salvo localmente primeiro; a sincronização é apenas enfileirada
              depois e nunca bloqueia o salvamento. Após o primeiro sucesso confirmado pela Omie, o
              vínculo é permanente e não pode ser desfeito.
            </label>
          </div>
        </section>

        </FormDialogBody>
        <FormDialogFooter>
          <a
            href="/cadastros"
            className="inline-flex items-center min-h-[44px] px-4 border border-slate-300 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </a>
          <button
            type="submit"
            disabled={salvando}
            className="inline-flex items-center min-h-[44px] px-5 bg-slate-900 text-white rounded-md text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            {salvando ? 'Salvando…' : 'Salvar cliente'}
          </button>
        </FormDialogFooter>
      </form>
      </FormDialogContent>
    </FormDialog>
  );
};
